import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Briefcase, Plus, FileText, Send, Download, Users, Trash2,
  MessageSquare, Copy, ExternalLink, CalendarClock, Phone, Mail,
  Building2, Hash, ArrowRight, ChevronRight, TrendingUp, Target, PenLine, Eye, Presentation, Pencil, ShieldCheck, MailCheck, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { AccessoryFieldset, AccessoryManager } from '@/components/sales/QuoteAccessories';
import { SolutionField, InvoiceFields, RenewalFields, DeliveryFields } from '@/components/sales/QuoteExtraFields';
import { QuoteTotalsSummary, SigningLogDialog, PreviewExtras } from '@/components/sales/QuoteAdminParts';
import { TodaySales } from '@/components/crm/TodaySales';
import { LeadFilters } from '@/components/crm/LeadFilters';
import { LeadDetailDialog } from '@/components/crm/LeadDetailDialog';
import { SalesStats } from '@/components/crm/SalesStats';
import { CrmSettingsDialog } from '@/components/crm/CrmSettingsDialog';
import { sek, fmtDate, PRIORITY, FOLLOW, filterLeads } from '@/components/crm/crmUtils';
import { computeQuoteTotals, renewalText, DEFAULT_SOLUTION_TEXT } from '@/lib/quoteCalc';

const API = process.env.REACT_APP_BACKEND_URL;

const QUOTE_EXTRA_DEFAULTS = {
  solution_text: DEFAULT_SOLUTION_TEXT, delivery_time: '', installation_included: null, installation_by: '',
  technical_requirements: '', wifi_required: false, wifi_requirements: '', delivery_comments: '',
  invoice_reference: '', invoice_address: '', po_number: '', payment_terms: '30 dagar netto',
  auto_renew: true, renewal_months: 12, notice_days: 90,
};
const EXTRA_KEYS = Object.keys(QUOTE_EXTRA_DEFAULTS);

const emptyQuoteForm = () => ({
    lead_id: '', school_name: '', org_number: '', contact_name: '', contact_email: '', reference: '',
    hardware_items: [{ description: 'Vapelarm sensor \u2013 WiFi', quantity: 10, unit_price: 2695, unit: 'st' }],
    accessory_items: [],
    license_items: [{ description: 'Vapelarm Cloud, licens, service & support \u2013 12 m\u00e5nader', quantity: 10, unit_price: 1068, unit: '\u00e5r' }],
    ...QUOTE_EXTRA_DEFAULTS,
    installation_cost: 0, discount_percent: 0, contract_months: 12, valid_days: 30, notes: '',});

const ITEM_KEYS = { hardware: 'hardware_items', accessory: 'accessory_items', license: 'license_items' };
const collectItems = (f) => Object.entries(ITEM_KEYS).flatMap(([type, key]) => (f[key] || []).map(i => ({ ...i, item_type: type })));

const STAGES = [
  { value: 'new', label: 'Ny', icon: '🔵', bg: 'from-slate-500/10 to-slate-500/5', border: 'border-slate-200', dot: 'bg-slate-400' },
  { value: 'contacted', label: 'Kontaktad', icon: '📞', bg: 'from-blue-500/10 to-blue-500/5', border: 'border-blue-200', dot: 'bg-blue-500' },
  { value: 'demo', label: 'Demo', icon: '🎯', bg: 'from-violet-500/10 to-violet-500/5', border: 'border-violet-200', dot: 'bg-violet-500' },
  { value: 'quote_sent', label: 'Offert', icon: '📄', bg: 'from-amber-500/10 to-amber-500/5', border: 'border-amber-200', dot: 'bg-amber-500' },
  { value: 'negotiation', label: 'Förhandling', icon: '🤝', bg: 'from-orange-500/10 to-orange-500/5', border: 'border-orange-200', dot: 'bg-orange-500' },
  { value: 'won', label: 'Vunnen', icon: '✅', bg: 'from-emerald-500/10 to-emerald-500/5', border: 'border-emerald-300', dot: 'bg-emerald-500' },
  { value: 'lost', label: 'Förlorad', icon: '❌', bg: 'from-red-500/10 to-red-500/5', border: 'border-red-200', dot: 'bg-red-400' },
  { value: 'paused', label: 'Pausad – återkom senare', icon: '⏸️', bg: 'from-slate-500/10 to-slate-500/5', border: 'border-slate-300', dot: 'bg-slate-500' },
];
const EMPTY_FILTERS = { search: '', quick: '', stage: '', priority: '', owner: '', minSensors: '', minValue: '', followBefore: '' };

const Q_STATUS = {
  draft: { label: 'Utkast', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', icon: PenLine },
  sent: { label: 'Skickad', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: Send },
  viewed: { label: 'Visad', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Eye },
  signed: { label: 'Signerad', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', icon: FileText },
  expired: { label: 'Utgången', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: CalendarClock },
};

function KpiCard({ value, label, icon: Icon, accent }) {
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-gradient-to-br ${accent} p-4`}>
      <Icon className="absolute -right-2 -top-2 h-16 w-16 opacity-[0.07]" strokeWidth={1.5} />
      <div className="text-3xl font-extrabold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function LeadCard({ lead, onStage, onActivity, onQuote, onDelete, onOpen }) {
  const c = lead.crm;
  const initials = (lead.contact_name || lead.school_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="group rounded-lg border bg-card p-3 hover:shadow-md transition-all duration-200 hover:border-blue-300/60" data-testid={`lead-${lead.id}`}>
      <div className="flex items-start gap-3 cursor-pointer" onClick={() => onOpen(lead)} data-testid={`lead-open-${lead.id}`}>
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{lead.school_name}</div>
          <div className="text-[11px] text-muted-foreground truncate">{lead.municipality}</div>
        </div>
        {c && <span title={`Prioritet ${c.priority.label}${c.priority.reasons.length ? ': ' + c.priority.reasons.join(', ') : ''}`} className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${PRIORITY[c.priority.level].dot}`} data-testid={`lead-priority-${lead.id}`} />}
      </div>
      {c && (lead.next_activity || lead.follow_up_date || c.reminders.length > 0) && (
        <div className={`mt-2 rounded-md border px-2 py-1 text-[11px] leading-tight ${FOLLOW[c.follow_status]}`} data-testid={`lead-next-${lead.id}`}>
          <div className="font-medium truncate">{lead.next_activity || c.reminders[0]?.title || 'Uppföljning'}</div>
          {lead.follow_up_date && <div className="opacity-80">{fmtDate(lead.follow_up_date)}{lead.follow_up_time ? ` ${lead.follow_up_time}` : ''}</div>}
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
        {lead.contact_name && <span className="flex items-center gap-1 truncate"><Users className="h-3 w-3" />{lead.contact_name}</span>}
        {lead.estimated_sensors > 0 && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{lead.estimated_sensors} st</span>}
        {c && c.value.first_year > 0 && <span className="ml-auto font-semibold text-foreground" data-testid={`lead-value-${lead.id}`}>{sek(c.value.first_year)}</span>}
      </div>
      {lead.contact_phone && <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{lead.contact_phone}</div>}
      {/* Actions — visible on hover */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-dashed opacity-0 group-hover:opacity-100 transition-opacity">
        <Select value="" onValueChange={v => onStage(lead.id, v)}>
          <SelectTrigger className="h-6 text-[10px] w-auto gap-1 border-0 px-1.5 bg-transparent hover:bg-accent"><ChevronRight className="h-3 w-3" /><span>Flytta</span></SelectTrigger>
          <SelectContent>{STAGES.filter(s => s.value !== lead.stage).map(s => <SelectItem key={s.value} value={s.value}>{s.icon} {s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => onActivity(lead.id)}><MessageSquare className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => onQuote(lead)}><FileText className="h-3 w-3 text-blue-500" /></Button>
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] ml-auto" onClick={() => onDelete(lead.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
      </div>
    </div>
  );
}

export default function SalesPortalPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('pipeline');
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [showAccessories, setShowAccessories] = useState(false);
  const [newAccessory, setNewAccessory] = useState({ name: '', unit_price: '', unit: 'st' });
  const [showNewLead, setShowNewLead] = useState(false);
  const [showNewQuote, setShowNewQuote] = useState(false);
  const [quoteStep, setQuoteStep] = useState('edit'); // 'edit' | 'preview'
  const [createdQuote, setCreatedQuote] = useState(null);
  const [showActivity, setShowActivity] = useState(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [activityText, setActivityText] = useState('');
  const [leadForm, setLeadForm] = useState({ school_name: '', municipality: '', contact_name: '', contact_email: '', contact_phone: '', estimated_sensors: '', notes: '' });
  const [quoteForm, setQuoteForm] = useState(emptyQuoteForm);
  const [crm, setCrm] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [detail, setDetail] = useState(null);
  const [showCrmSettings, setShowCrmSettings] = useState(false);
  const [statsKey, setStatsKey] = useState(0);

  const [editingQuote, setEditingQuote] = useState(null);
  const [signLog, setSignLog] = useState(null);
  const setQF = (patch) => setQuoteForm(f => ({ ...f, ...patch }));
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    const [lr, qr, dr, rr, tr, ar] = await Promise.all([
      fetch(`${API}/api/sales/crm/overview`, { headers: h }),
      fetch(`${API}/api/sales/quotes`, { headers: h }),
      fetch(`${API}/api/sales/dashboard`, { headers: h }),
      fetch(`${API}/api/sales/renewals`, { headers: h }),
      fetch(`${API}/api/sales/templates`, { headers: h }),
      fetch(`${API}/api/sales/accessories`, { headers: h }),
    ]);
    if (lr.ok) { const ov = await lr.json(); setLeads(ov.leads); setCrm(ov); }
    setStatsKey(k => k + 1);
    if (qr.ok) setQuotes(await qr.json());
    if (dr.ok) setDashboard(await dr.json());
    if (rr.ok) setRenewals(await rr.json());
    if (tr.ok) setTemplates(await tr.json());
    if (ar.ok) setAccessories(await ar.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // CRUD functions
  const createLead = async () => {
    const body = { ...leadForm, estimated_sensors: parseInt(leadForm.estimated_sensors) || 0 };
    const r = await fetch(`${API}/api/sales/leads`, { method: 'POST', headers: h, body: JSON.stringify(body) });
    if (r.ok) { toast.success('Lead skapad'); setShowNewLead(false); setLeadForm({ school_name: '', municipality: '', contact_name: '', contact_email: '', contact_phone: '', estimated_sensors: '', notes: '' }); load(); }
  };
  const reminderAction = async (id, key, action, days) => {
    await fetch(`${API}/api/sales/leads/${id}/reminder`, { method: 'POST', headers: h, body: JSON.stringify({ key, action, days: days || 1 }) });
    toast.success(action === 'done' ? 'Markerad som klar' : `Påminnelsen flyttad ${days === 7 ? 'en vecka' : 'en dag'}`); load();
  };
  const updateStage = async (id, stage) => { if (stage === 'paused') { const l = leads.find(x => x.id === id); setDetail({ id, tab: 'sales' }); toast.info(`Ange orsak och datum för återkontakt för ${l?.school_name || 'leadet'}`); return; } await fetch(`${API}/api/sales/leads/${id}`, { method: 'PUT', headers: h, body: JSON.stringify({ stage }) }); load(); };
  const addActivity = async () => { if (!activityText.trim()) return; await fetch(`${API}/api/sales/leads/${showActivity}/activity`, { method: 'POST', headers: h, body: JSON.stringify({ text: activityText }) }); setActivityText(''); setShowActivity(null); load(); };
  const deleteLead = async (id) => { if (!window.confirm('Ta bort lead?')) return; await fetch(`${API}/api/sales/leads/${id}`, { method: 'DELETE', headers: h }); load(); };
  const deleteQuote = async (id) => { if (!window.confirm('Ta bort offert?')) return; const r = await fetch(`${API}/api/sales/quotes/${id}`, { method: 'DELETE', headers: h }); if (r.ok) { toast.success('Offert borttagen'); load(); } };

  const createQuote = async () => {
    const allItems = collectItems(quoteForm);
    const body = {
      lead_id: quoteForm.lead_id || undefined,
      school_name: quoteForm.school_name, org_number: quoteForm.org_number,
      contact_name: quoteForm.contact_name, contact_email: quoteForm.contact_email,
      reference: quoteForm.reference, items: allItems,
      installation_cost: parseFloat(quoteForm.installation_cost) || 0,
      discount_percent: parseFloat(quoteForm.discount_percent) || 0,
      contract_months: parseInt(quoteForm.contract_months) || 12,
      valid_days: parseInt(quoteForm.valid_days) || 30,
      notes: quoteForm.notes,
      ...Object.fromEntries(EXTRA_KEYS.map(k => [k, quoteForm[k]])),
      renewal_months: parseInt(quoteForm.renewal_months) || 12,
      notice_days: parseInt(quoteForm.notice_days) || 0,
    };
    const url = editingQuote ? `${API}/api/sales/quotes/${editingQuote.id}` : `${API}/api/sales/quotes`;
    const r = await fetch(url, { method: editingQuote ? 'PUT' : 'POST', headers: h, body: JSON.stringify(body) });
    if (r.ok) {
      const q = await r.json();
      toast.success(editingQuote ? `Offert ${q.quote_number} uppdaterad (version ${q.version})` : `Offert ${q.quote_number} skapad`);
      setShowNewQuote(false); setQuoteStep('edit');
      if (editingQuote) { setEditingQuote(null); setQuoteForm(emptyQuoteForm()); } else setCreatedQuote(q);
      load();
    } else {
      const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Kunde inte spara offerten');
    }
  };

  const openEditQuote = async (id) => {
    const r = await fetch(`${API}/api/sales/quotes/${id}`, { headers: h });
    if (!r.ok) { toast.error('Kunde inte hämta offerten'); return; }
    const q = await r.json();
    const pick = (t) => (q.items || []).filter(i => (i.item_type || 'hardware') === t).map(({ description, quantity, unit_price, unit }) => ({ description, quantity, unit_price, unit }));
    const days = q.valid_days || 30;
    setQuoteForm({
      lead_id: q.lead_id || '', school_name: q.school_name || '', org_number: q.org_number || '',
      contact_name: q.contact_name || '', contact_email: q.contact_email || '', reference: q.reference || '',
      hardware_items: pick('hardware'), accessory_items: pick('accessory'), license_items: pick('license'),
      ...Object.fromEntries(EXTRA_KEYS.map(k => [k, q[k] ?? QUOTE_EXTRA_DEFAULTS[k]])),
      ...(q.auto_renew === undefined ? { auto_renew: true } : {}),
      installation_cost: q.installation_cost || 0, discount_percent: q.discount_percent || 0,
      contract_months: q.contract_months || 12, valid_days: days, notes: q.notes || '',
    });
    setEditingQuote(q); setQuoteStep('edit'); setShowNewQuote(true);
  };

  const resendConfirmation = async (id) => {
    if (!window.confirm('Skicka orderbekräftelsen igen till kunden (kopia till info@vapelarm.se)?')) return;
    const r = await fetch(`${API}/api/sales/quotes/${id}/order-confirmation`, { method: 'POST', headers: h });
    const d = await r.json().catch(() => ({}));
    if (r.ok) toast.success('Orderbekräftelsen skickades'); else toast.error(d.detail || 'Kunde inte skicka');
    load();
  };

  const openSignLog = async (id) => {
    const r = await fetch(`${API}/api/sales/quotes/${id}/signing-log`, { headers: h });
    setSignLog(r.ok ? await r.json() : []);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    const allItems = collectItems(quoteForm);
    const body = { name: templateName, items: allItems, installation_cost: parseFloat(quoteForm.installation_cost) || 0, contract_months: parseInt(quoteForm.contract_months) || 12, notes: quoteForm.notes };
    const r = await fetch(`${API}/api/sales/templates`, { method: 'POST', headers: h, body: JSON.stringify(body) });
    if (r.ok) { toast.success('Mall sparad'); setShowSaveTemplate(false); setTemplateName(''); load(); }
  };

  const applyTemplate = (tpl) => {
    const hw = (tpl.items || []).filter(i => i.item_type === 'hardware' || !i.item_type).map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, unit: i.unit || 'st' }));
    const lic = (tpl.items || []).filter(i => i.item_type === 'license').map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, unit: i.unit || 'ar' }));
    const acc = (tpl.items || []).filter(i => i.item_type === 'accessory').map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, unit: i.unit || 'st' }));
    setQuoteForm({ ...quoteForm, hardware_items: hw.length ? hw : quoteForm.hardware_items, accessory_items: acc, license_items: lic.length ? lic : quoteForm.license_items, installation_cost: tpl.installation_cost || 0, contract_months: tpl.contract_months || 12, notes: tpl.notes || quoteForm.notes });
    toast.success(`Mall "${tpl.name}" tillämpad`);
  };

  const sendQuote = async (id) => {
    try {
      const r = await fetch(`${API}/api/sales/quotes/${id}/send`, { method: 'POST', headers: h });
      if (r.ok) {
        const d = await r.json();
        if (d.email_sent) {
          toast.success(`Offert skickad via e-post till ${d.email_error || 'kund'}`);
        } else {
          toast.error(d.email_error || 'E-post kunde inte skickas — kontrollera SendGrid-konfiguration');
        }
        load();
        try {
          const url = `${window.location.origin}/quote/${d.public_token}`;
          await navigator.clipboard.writeText(url);
          toast.info('Signeringslänk kopierad!');
        } catch (_) { /* clipboard not available */ }
      } else {
        const err = await r.text();
        toast.error(`Kunde inte skicka offerten: ${err}`);
      }
    } catch (e) {
      toast.error(`Nätverksfel — kunde inte skicka offerten: ${e.message}`);
    }
  };
  const copyLink = async (tkn) => { try { await navigator.clipboard.writeText(`${window.location.origin}/quote/${tkn}`); toast.success('Länk kopierad!'); } catch (_) { toast.error('Kunde inte kopiera länken'); } };
  const downloadPdf = async (id) => {
    try {
      const r = await fetch(`${API}/api/sales/quotes/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('PDF-nedladdning misslyckades');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offert-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Kunde inte ladda ner PDF');
    }
  };

  const prefillQuote = (lead) => {
    setQuoteForm({
      ...quoteForm, lead_id: lead.id, school_name: lead.school_name,
      contact_name: lead.contact_name, contact_email: lead.contact_email,
      hardware_items: [{ description: 'Vapelarm sensor \u2013 WiFi', quantity: lead.estimated_sensors || 10, unit_price: 2695, unit: 'st' }],
      license_items: [{ description: 'Vapelarm Cloud, licens, service & support \u2013 12 m\u00e5nader', quantity: lead.estimated_sensors || 10, unit_price: 1068, unit: '\u00e5r' }],
    });
    setQuoteStep('edit');
    setShowNewQuote(true);
  };

  const addItem = (type, preset) => { const key = ITEM_KEYS[type]; const row = preset || { description: '', quantity: 1, unit_price: 0, unit: type === 'license' ? 'ar' : 'st' }; setQuoteForm({ ...quoteForm, [key]: [...quoteForm[key], row] }); };
  const updateItem = (type, idx, field, val) => { const key = ITEM_KEYS[type]; const items = [...quoteForm[key]]; items[idx] = { ...items[idx], [field]: field === 'description' || field === 'unit' ? val : parseFloat(val) || 0 }; setQuoteForm({ ...quoteForm, [key]: items }); };
  const removeItem = (type, idx) => { const key = ITEM_KEYS[type]; setQuoteForm({ ...quoteForm, [key]: quoteForm[key].filter((_, i) => i !== idx) }); };

  const saveAccessory = async (acc) => {
    const r = await fetch(`${API}/api/sales/accessories`, { method: 'POST', headers: h, body: JSON.stringify(acc) });
    if (r.ok) { toast.success(`"${acc.name}" sparat i snabblistan`); load(); return true; }
    const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Kunde inte spara tillbehöret'); return false;
  };
  const deleteAccessory = async (id) => { const r = await fetch(`${API}/api/sales/accessories/${id}`, { method: 'DELETE', headers: h }); if (r.ok) load(); };

  const activeStages = STAGES.filter(s => !['won', 'lost', 'paused'].includes(s.value));
  const closedStages = STAGES.filter(s => ['won', 'lost', 'paused'].includes(s.value));
  const shownLeads = filterLeads(leads, crm?.kpis, filters);
  const detailLead = detail ? leads.find(l => l.id === detail.id) : null;

  return (
    <div className="space-y-6" data-testid="sales-portal-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
              <Briefcase className="h-5 w-5 text-white" />
            </div>
            Säljportal
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 ml-[46px]">Pipeline, offerter & förnyelser</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const res = await fetch(`${API}/api/sales/presentation-pdf`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) throw new Error();
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'Vapelarm_Presentation.pdf';
              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
              toast.success('Presentation nedladdad');
            } catch { toast.error('Kunde inte ladda ner'); }
          }} data-testid="download-presentation-btn">
            <Download className="h-4 w-4 mr-1.5" /> Presentation PDF
          </Button>
          <Button variant="outline" onClick={() => setShowCrmSettings(true)} data-testid="crm-settings-btn">
            <Settings2 className="h-4 w-4 mr-1.5" /> Prislista
          </Button>
          <Button onClick={() => setShowNewLead(true)} data-testid="new-lead-btn" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md">
            <Plus className="h-4 w-4 mr-1.5" /> Ny lead
          </Button>
        </div>
      </div>

      <TodaySales crm={crm} leads={leads} onOpenLead={l => setDetail({ id: l.id, tab: 'sales' })} onReminder={reminderAction} />

      {/* KPI row */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard value={dashboard.total_leads} label="Leads" icon={Users} accent="from-slate-100 to-white dark:from-slate-900 dark:to-slate-800" />
          <KpiCard value={dashboard.pipeline_sensors} label="Sensorer i pipeline" icon={Target} accent="from-blue-50 to-white dark:from-blue-950 dark:to-slate-800" />
          <KpiCard value={dashboard.total_quotes} label="Offerter" icon={FileText} accent="from-violet-50 to-white dark:from-violet-950 dark:to-slate-800" />
          <KpiCard value={dashboard.signed_quotes} label="Signerade" icon={PenLine} accent="from-emerald-50 to-white dark:from-emerald-950 dark:to-slate-800" />
          <KpiCard value={dashboard.won_sensors} label="Vunna sensorer" icon={TrendingUp} accent="from-emerald-50 to-white dark:from-emerald-950 dark:to-slate-800" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted/50 flex-wrap h-auto">
          <TabsTrigger value="pipeline" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Pipeline</TabsTrigger>
          <TabsTrigger value="quotes" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Offerter</TabsTrigger>
          <TabsTrigger value="renewals" className="gap-1.5 relative">
            <CalendarClock className="h-3.5 w-3.5" /> Förnyelser
            {renewals.length > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">{renewals.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="stats" className="gap-1.5" data-testid="tab-stats"><TrendingUp className="h-3.5 w-3.5" /> Statistik</TabsTrigger>
        </TabsList>

        {/* ===== PIPELINE ===== */}
        <TabsContent value="pipeline" className="mt-4">
          <LeadFilters f={filters} setF={setFilters} stages={STAGES} sellers={crm?.sellers || []} kpis={crm?.kpis} />
          {/* Kanban columns */}
          <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 320 }}>
            {activeStages.map(stage => {
              const stageLeads = shownLeads.filter(l => l.stage === stage.value);
              return (
                <div key={stage.value} className={`flex-shrink-0 w-64 rounded-xl border ${stage.border} bg-gradient-to-b ${stage.bg} p-3`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${stage.dot}`} />
                      <span className="font-semibold text-sm">{stage.label}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] px-1.5 h-5">{stageLeads.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {stageLeads.length === 0 && <p className="text-xs text-muted-foreground text-center py-6 opacity-50">Inga leads</p>}
                    {stageLeads.map(lead => (
                      <LeadCard key={lead.id} lead={lead} onStage={updateStage} onActivity={setShowActivity} onQuote={prefillQuote} onDelete={deleteLead} onOpen={l => setDetail({ id: l.id, tab: 'customer' })} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Won / Lost collapsed */}
          {closedStages.map(stage => {
            const stageLeads = shownLeads.filter(l => l.stage === stage.value);
            if (stageLeads.length === 0) return null;
            return (
              <div key={stage.value} className="mt-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-2 w-2 rounded-full ${stage.dot}`} />
                  <span className="text-sm font-medium">{stage.icon} {stage.label}</span>
                  <Badge variant="outline" className="text-[10px]">{stageLeads.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {stageLeads.map(l => (
                    <div key={l.id} className="flex items-center gap-2 rounded-lg border p-2 text-sm">
                      <button className="font-medium truncate flex-1 text-left hover:underline" onClick={() => setDetail({ id: l.id, tab: stage.value === 'paused' ? 'sales' : 'customer' })} data-testid={`closed-lead-${l.id}`}>{l.school_name}</button>
                      {stage.value === 'paused' && l.resume_date && <span className="text-[11px] text-muted-foreground">Återkom {fmtDate(l.resume_date)}</span>}
                      <span className="text-xs text-muted-foreground">{l.estimated_sensors} st</span>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => deleteLead(l.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* ===== QUOTES ===== */}
        <TabsContent value="quotes" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowNewQuote(true)} data-testid="new-quote-btn" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
              <Plus className="h-4 w-4 mr-1" /> Ny offert
            </Button>
          </div>
          {quotes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Inga offerter ännu</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {quotes.map(q => {
                const st = Q_STATUS[q.status] || Q_STATUS.draft;
                const StIcon = st.icon;
                const hw = q.hardware_total || 0;
                const lic = q.license_total || 0;
                const sum = q.sum_ex_vat ?? (hw - (q.discount_amount || 0) + (q.accessory_total || 0) + lic + (q.installation_cost || 0));
                const inkl = Math.round(q.sum_inkl_vat ?? sum * 1.25);
                return (
                  <div key={q.id} className="group flex items-center gap-4 rounded-xl border bg-card p-4 hover:shadow-md transition-all duration-200 hover:border-blue-300/60" data-testid={`quote-${q.id}`}>
                    <div className="h-10 w-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <StIcon className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{q.quote_number}</span>
                        <Badge className={`${st.cls} text-[10px]`}>{st.label}</Badge>
                        {q.version > 1 && <span className="text-[10px] text-muted-foreground">v{q.version}</span>}
                        {q.order_confirmation && (
                          <span className={`text-[10px] ${q.order_confirmation.sent ? 'text-emerald-600' : 'text-red-600'}`} title={q.order_confirmation.error || (q.order_confirmation.to || []).join(', ')} data-testid={`quote-oc-status-${q.id}`}>
                            {q.order_confirmation.sent ? 'Orderbekräftelse skickad' : 'Orderbekräftelse misslyckades'}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{q.school_name} — {q.contact_name}</div>
                    </div>
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <div className="text-sm font-bold">{inkl.toLocaleString('sv-SE')} kr</div>
                      <div className="text-[10px] text-muted-foreground">inkl. moms</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => sendQuote(q.id)}><Send className="h-3 w-3" /> Maila</Button>
                      {q.public_token && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => copyLink(q.public_token)} title="Kopiera länk"><Copy className="h-3 w-3" /></Button>}
                      {q.public_token && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => window.open(`/quote/${q.public_token}`, '_blank')} title="Visa"><ExternalLink className="h-3 w-3" /></Button>}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadPdf(q.id)} title="PDF"><Download className="h-3 w-3" /></Button>
                      {q.status === 'signed' || q.signed_at
                        ? <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openSignLog(q.id)} title="Signeringslogg" data-testid={`quote-signlog-${q.id}`}><ShieldCheck className="h-3 w-3 text-emerald-600" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => resendConfirmation(q.id)} title="Skicka orderbekräftelse igen" data-testid={`quote-resend-oc-${q.id}`}><MailCheck className="h-3 w-3" /></Button>
                          </>
                        : <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditQuote(q.id)} title="Redigera" data-testid={`quote-edit-${q.id}`}><Pencil className="h-3 w-3" /></Button>}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => deleteQuote(q.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== RENEWALS ===== */}
        <TabsContent value="stats" className="mt-4">
          <SalesStats token={token} refreshKey={statsKey} />
        </TabsContent>

        <TabsContent value="renewals" className="mt-4 space-y-3">
          {renewals.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarClock className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Inga avtal att förnya inom 90 dagar</p>
            </div>
          ) : renewals.map(q => {
            const pct = Math.max(0, Math.min(100, (90 - (q.days_until_expiry || 0)) / 90 * 100));
            const barColor = q.urgency === 'critical' ? 'bg-red-500' : q.urgency === 'warning' ? 'bg-amber-500' : 'bg-blue-500';
            return (
              <div key={q.id} className="rounded-xl border p-4 flex items-center gap-4" data-testid={`renewal-${q.id}`}>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{q.school_name}</div>
                  <div className="text-xs text-muted-foreground">{q.quote_number} — {q.contact_name} — {(q.subtotal || 0).toLocaleString()} kr/period</div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="text-center flex-shrink-0">
                  <div className="text-2xl font-extrabold">{q.days_until_expiry}</div>
                  <div className="text-[10px] text-muted-foreground">dagar kvar</div>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}
      {/* New Lead */}
      <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ny lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><Input placeholder="Skolans namn" value={leadForm.school_name} onChange={e => setLeadForm({...leadForm, school_name: e.target.value})} data-testid="lead-school-input" /></div>
            <Input placeholder="Kommun" value={leadForm.municipality} onChange={e => setLeadForm({...leadForm, municipality: e.target.value})} />
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /><Input placeholder="Kontaktperson" value={leadForm.contact_name} onChange={e => setLeadForm({...leadForm, contact_name: e.target.value})} /></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><Input placeholder="Telefon" value={leadForm.contact_phone} onChange={e => setLeadForm({...leadForm, contact_phone: e.target.value})} /></div>
            </div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><Input placeholder="E-post" value={leadForm.contact_email} onChange={e => setLeadForm({...leadForm, contact_email: e.target.value})} /></div>
            <Input placeholder="Uppskattat antal sensorer" type="number" value={leadForm.estimated_sensors} onChange={e => setLeadForm({...leadForm, estimated_sensors: e.target.value})} />
            <Textarea placeholder="Anteckningar" value={leadForm.notes} onChange={e => setLeadForm({...leadForm, notes: e.target.value})} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLead(false)}>Avbryt</Button>
            <Button onClick={createLead} disabled={!leadForm.school_name} data-testid="create-lead-btn">Skapa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity */}
      <Dialog open={!!showActivity} onOpenChange={() => setShowActivity(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lägg till notering</DialogTitle></DialogHeader>
          <Textarea placeholder="T.ex. 'Ringde kunden, vill ha demo nästa vecka'" value={activityText} onChange={e => setActivityText(e.target.value)} rows={3} data-testid="activity-input" />
          <DialogFooter><Button variant="outline" onClick={() => setShowActivity(null)}>Avbryt</Button><Button onClick={addActivity} disabled={!activityText.trim()}>Spara</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== NEW QUOTE DIALOG — Edit + Preview ===== */}
      <Dialog open={showNewQuote} onOpenChange={v => { setShowNewQuote(v); if (!v) { setQuoteStep('edit'); if (editingQuote) { setEditingQuote(null); setQuoteForm(emptyQuoteForm()); } } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {quoteStep === 'edit' ? (<>
            <DialogHeader><DialogTitle>{editingQuote ? `Redigera offert ${editingQuote.quote_number} (version ${editingQuote.version || 1})` : 'Ny offert'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {/* Templates */}
              {templates.length > 0 && (
                <div className="flex items-center gap-2 p-2.5 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-200/50 border-dashed">
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-400 whitespace-nowrap">Mall:</span>
                  {templates.map(tpl => <Button key={tpl.id} size="sm" variant="outline" className="text-xs h-7 border-blue-200" onClick={() => applyTemplate(tpl)}>{tpl.name}</Button>)}
                </div>
              )}
              {/* Customer info */}
              <fieldset className="border rounded-lg p-3 space-y-2">
                <legend className="text-xs font-semibold px-1 text-muted-foreground">Kunduppgifter</legend>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[11px] font-medium text-muted-foreground">Skola / organisation *</label><Input value={quoteForm.school_name} onChange={e => setQuoteForm({...quoteForm, school_name: e.target.value})} data-testid="quote-school-input" /></div>
                  <div><label className="text-[11px] font-medium text-muted-foreground">Organisationsnummer</label><Input value={quoteForm.org_number} onChange={e => setQuoteForm({...quoteForm, org_number: e.target.value})} placeholder="XXXXXX-XXXX" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[11px] font-medium text-muted-foreground">Kontaktperson *</label><Input value={quoteForm.contact_name} onChange={e => setQuoteForm({...quoteForm, contact_name: e.target.value})} /></div>
                  <div><label className="text-[11px] font-medium text-muted-foreground">E-post *</label><Input value={quoteForm.contact_email} onChange={e => setQuoteForm({...quoteForm, contact_email: e.target.value})} type="email" /></div>
                </div>
                <div><label className="text-[11px] font-medium text-muted-foreground">Referens / inköpsreferens</label><Input value={quoteForm.reference} onChange={e => setQuoteForm({...quoteForm, reference: e.target.value})} /></div>
              </fieldset>

              {/* Hardware items */}
              <fieldset className="border rounded-lg p-3 space-y-2">
                <legend className="text-xs font-semibold px-1 text-muted-foreground">Hårdvara (engångskostnad)</legend>
                {quoteForm.hardware_items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-end">
                    <div className="col-span-5"><label className="text-[10px] text-muted-foreground">Beskrivning</label><Input className="text-xs" value={item.description} onChange={e => updateItem('hardware', i, 'description', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Antal</label><Input className="text-xs" type="number" min="1" value={item.quantity} onChange={e => updateItem('hardware', i, 'quantity', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Enhet</label><Input className="text-xs" value={item.unit} onChange={e => updateItem('hardware', i, 'unit', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Pris (SEK)</label><Input className="text-xs" type="number" min="0" value={item.unit_price} onChange={e => updateItem('hardware', i, 'unit_price', e.target.value)} /></div>
                    <Button size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0" onClick={() => removeItem('hardware', i)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="text-xs" onClick={() => addItem('hardware')}><Plus className="h-3 w-3 mr-1" /> Lägg till rad</Button>
              </fieldset>

              <AccessoryFieldset
                items={quoteForm.accessory_items}
                accessories={accessories}
                onAdd={(preset) => addItem('accessory', preset)}
                onUpdate={(i, f, v) => updateItem('accessory', i, f, v)}
                onRemove={(i) => removeItem('accessory', i)}
                onSave={(it) => saveAccessory({ name: it.description, unit_price: it.unit_price, unit: it.unit || 'st' })}
                onManage={() => setShowAccessories(true)}
              />

              {/* License items */}
              <fieldset className="border rounded-lg p-3 space-y-2 border-blue-200/60 bg-blue-50/20 dark:bg-blue-950/10">
                <legend className="text-xs font-semibold px-1 text-blue-700 dark:text-blue-400">Vapelarm Cloud (löpande kostnad)</legend>
                {quoteForm.license_items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-end">
                    <div className="col-span-5"><label className="text-[10px] text-muted-foreground">Beskrivning</label><Input className="text-xs" value={item.description} onChange={e => updateItem('license', i, 'description', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Antal sensorer</label><Input className="text-xs" type="number" min="1" value={item.quantity} onChange={e => updateItem('license', i, 'quantity', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">Period</label><Input className="text-xs" value={item.unit} onChange={e => updateItem('license', i, 'unit', e.target.value)} /></div>
                    <div className="col-span-2"><label className="text-[10px] text-muted-foreground">SEK / sensor / år</label><Input className="text-xs" type="number" min="0" value={item.unit_price} onChange={e => updateItem('license', i, 'unit_price', e.target.value)} /></div>
                    <Button size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0" onClick={() => removeItem('license', i)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                  </div>
                ))}
                <p className="text-[10px] text-muted-foreground">89 SEK exkl. moms / sensor / månad = 1 068 SEK / sensor / 12 månader</p>
                <Button size="sm" variant="outline" className="text-xs" onClick={() => addItem('license')}><Plus className="h-3 w-3 mr-1" /> Lägg till rad</Button>
              </fieldset>

              {/* Settings row */}
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-[11px] font-medium text-muted-foreground">Rabatt (%)</label><Input type="number" min="0" max="100" value={quoteForm.discount_percent} onChange={e => setQuoteForm({...quoteForm, discount_percent: e.target.value})} /><p className="text-[10px] text-muted-foreground mt-0.5">Gäller hårdvara</p></div>
                <div><label className="text-[11px] font-medium text-muted-foreground">Avtalstid (månader)</label><Input type="number" min="1" value={quoteForm.contract_months} onChange={e => setQuoteForm({...quoteForm, contract_months: e.target.value})} /></div>
                <div><label className="text-[11px] font-medium text-muted-foreground">Offert giltig (dagar)</label><Input type="number" min="1" value={quoteForm.valid_days} onChange={e => setQuoteForm({...quoteForm, valid_days: e.target.value})} /></div>
              </div>

              <SolutionField form={quoteForm} set={setQF} />
              <DeliveryFields form={quoteForm} set={setQF} />
              <InvoiceFields form={quoteForm} set={setQF} />
              <RenewalFields form={quoteForm} set={setQF} />

              <div><label className="text-[11px] font-medium text-muted-foreground">Kundspecifika noteringar (valfritt)</label><Textarea value={quoteForm.notes} onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})} rows={2} placeholder="T.ex. särskild installation, leveransinfo, specialpris" /></div>

              {/* LIVE SUMMARY */}
              <QuoteTotalsSummary t={computeQuoteTotals(quoteForm)} testId="quote-live-summary" />
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setTemplateName(''); setShowSaveTemplate(true); }}>Spara som mall</Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => { setShowNewQuote(false); setQuoteStep('edit'); if (editingQuote) { setEditingQuote(null); setQuoteForm(emptyQuoteForm()); } }}>Avbryt</Button>
              <Button
                onClick={() => setQuoteStep('preview')}
                disabled={!quoteForm.school_name.trim() || !quoteForm.contact_name.trim() || !quoteForm.contact_email.trim() || (quoteForm.hardware_items.length === 0 && quoteForm.license_items.length === 0 && quoteForm.accessory_items.length === 0) || quoteForm.hardware_items.some(i => i.quantity <= 0 || i.unit_price < 0) || quoteForm.accessory_items.some(i => !i.description.trim() || i.quantity <= 0 || i.unit_price < 0) || quoteForm.license_items.some(i => i.quantity <= 0 || i.unit_price < 0)}
                data-testid="preview-quote-btn"
              >Förhandsgranska offert</Button>
            </DialogFooter>
          </>) : (<>
            {/* ===== PREVIEW ===== */}
            <DialogHeader><DialogTitle>Förhandsgranskning</DialogTitle></DialogHeader>
            {(() => {
              const totals = computeQuoteTotals(quoteForm);
              const months = parseInt(quoteForm.contract_months) || 12;
              const validDays = parseInt(quoteForm.valid_days) || 30;
              const today = new Date();
              const validUntil = new Date(today.getTime() + validDays * 86400000);
              return (
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-lg">VAPELARM</div>
                      <div className="text-[10px] text-muted-foreground">Intelligent vape-detektion för skolor</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Offertdatum: {today.toLocaleDateString('sv-SE')}</div>
                      <div className="text-xs text-muted-foreground">Giltig t.o.m.: {validUntil.toLocaleDateString('sv-SE')}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs border rounded-lg p-3 bg-slate-50 dark:bg-slate-900">
                    <div><span className="text-muted-foreground">Skola / organisation:</span> <strong>{quoteForm.school_name}</strong></div>
                    <div><span className="text-muted-foreground">Org.nr:</span> {quoteForm.org_number || '–'}</div>
                    <div><span className="text-muted-foreground">Kontakt:</span> {quoteForm.contact_name}</div>
                    <div><span className="text-muted-foreground">E-post:</span> {quoteForm.contact_email}</div>
                    {quoteForm.reference && <div><span className="text-muted-foreground">Referens:</span> {quoteForm.reference}</div>}
                  </div>

                  {quoteForm.hardware_items.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wider">Hårdvara (engångskostnad)</div>
                      <div className="border rounded-lg overflow-hidden">
                        {quoteForm.hardware_items.map((it, i) => (
                          <div key={i} className={`flex justify-between px-3 py-1.5 text-xs ${i%2===0 ? 'bg-slate-50 dark:bg-slate-900' : ''}`}>
                            <span>{it.description}</span>
                            <span className="font-medium">{it.quantity} {it.unit} × {it.unit_price.toLocaleString('sv-SE')} = {(it.quantity * it.unit_price).toLocaleString('sv-SE')} SEK</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {quoteForm.accessory_items.length > 0 && (
                    <div data-testid="preview-accessories">
                      <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wider">Tillbehör (engångskostnad)</div>
                      <div className="border rounded-lg overflow-hidden">
                        {quoteForm.accessory_items.map((it, i) => (
                          <div key={i} className={`flex justify-between px-3 py-1.5 text-xs ${i%2===0 ? 'bg-slate-50 dark:bg-slate-900' : ''}`}>
                            <span>{it.description}</span>
                            <span className="font-medium">{it.quantity} {it.unit} × {it.unit_price.toLocaleString('sv-SE')} = {(it.quantity * it.unit_price).toLocaleString('sv-SE')} SEK</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {quoteForm.license_items.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold mb-1 text-blue-700 dark:text-blue-400 uppercase tracking-wider">Vapelarm Cloud ({months} månader)</div>
                      <div className="border border-blue-200/60 rounded-lg overflow-hidden">
                        {quoteForm.license_items.map((it, i) => (
                          <div key={i} className={`flex justify-between px-3 py-1.5 text-xs ${i%2===0 ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}`}>
                            <span>{it.description}</span>
                            <span className="font-medium">{it.quantity} {it.unit} × {it.unit_price.toLocaleString('sv-SE')} SEK = {(it.quantity * it.unit_price).toLocaleString('sv-SE')} SEK</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <QuoteTotalsSummary t={totals} testId="quote-preview-summary" />
                  {quoteForm.solution_text && <div className="rounded-lg border p-3"><div className="text-xs font-semibold mb-1">Om lösningen</div><p className="text-xs text-muted-foreground">{quoteForm.solution_text}</p></div>}
                  <PreviewExtras f={quoteForm} />

                  <div className="text-[11px] text-muted-foreground space-y-1 border-t pt-3">
                    <p><strong>Avtalstid:</strong> {months} månader. {renewalText(quoteForm)}</p>
                    <p><strong>Garanti:</strong> Vapelarm lämnar 3 års garanti på levererad sensorhårdvara.</p>
                    <p><strong>Betalningsvillkor:</strong> {quoteForm.payment_terms || '30 dagar netto'}. Samtliga priser exkl. moms.</p>
                    <p className="italic">För beställningen gäller Vapelarm AB:s Allmänna villkor vilka bifogas offerten.</p>
                    {quoteForm.notes && <p><strong>Kundspecifikt:</strong> {quoteForm.notes}</p>}
                  </div>
                </div>
              );
            })()}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => setQuoteStep('edit')}><ArrowRight className="h-3 w-3 mr-1 rotate-180" /> Redigera</Button>
              <div className="flex-1" />
              <Button onClick={createQuote} data-testid="create-quote-btn" className="bg-gradient-to-r from-blue-600 to-indigo-600">{editingQuote ? 'Spara ändringar' : 'Skapa offert'}</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      <SigningLogDialog log={signLog} onClose={() => setSignLog(null)} />
      {crm && <LeadDetailDialog lead={detailLead} crm={crm} token={token} initialTab={detail?.tab || 'customer'}
        quotes={detailLead ? quotes.filter(q => q.lead_id === detailLead.id) : []} onClose={() => setDetail(null)} onSaved={load} />}
      {crm && <CrmSettingsDialog open={showCrmSettings} onClose={() => setShowCrmSettings(false)} settings={crm.settings} token={token} onSaved={load} />}

      <AccessoryManager
        open={showAccessories} onOpenChange={setShowAccessories}
        accessories={accessories} form={newAccessory} setForm={setNewAccessory}
        onCreate={async () => { if (await saveAccessory({ ...newAccessory, unit_price: parseFloat(newAccessory.unit_price) || 0 })) setNewAccessory({ name: '', unit_price: '', unit: 'st' }); }}
        onDelete={deleteAccessory}
      />

      {/* Save Template */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Spara som offertmall</DialogTitle></DialogHeader>
          <Input placeholder="Mallnamn (t.ex. 'Bas-paket 10 sensorer')" value={templateName} onChange={e => setTemplateName(e.target.value)} data-testid="template-name-input" />
          <p className="text-xs text-muted-foreground">Sparar aktuella rader, priser och villkor som återanvändbar mall.</p>
          <DialogFooter><Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Avbryt</Button><Button onClick={saveAsTemplate} disabled={!templateName.trim()}>Spara mall</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
